import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type TouchEvent } from "react";

import { CommandPalette, type Command } from "./components/CommandPalette";
import { Composer, type ComposerHandle } from "./components/Composer";
import { Conversation } from "./components/Conversation";
import { DebugDrawer } from "./components/DebugDrawer";
import { Landing } from "./components/Landing";
import { Onboarding, hasOnboarded, markOnboarded } from "./components/Onboarding";
import { Settings } from "./components/Settings";
import { Sidebar } from "./components/Sidebar";
import { ThreadSkeleton } from "./components/Skeleton";
import { TopBar } from "./components/TopBar";
import { fadeRise } from "./lib/motion";
import { playCue } from "./lib/sound";
import { useKeyboardShortcuts } from "./state/useKeyboardShortcuts";
import { useVoss } from "./state/VossProvider";

export function App(): JSX.Element {
  const { ready, newChat, toggleTheme, debug, setDebug, workspaces, selectWorkspace, conversations, selectConversation } = useVoss();
  const [entered, setEntered] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [onboarding, setOnboarding] = useState(() => !hasOnboarded());
  const composerRef = useRef<ComposerHandle>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const openPalette = useCallback(() => {
    playCue("open");
    setPaletteOpen(true);
  }, []);

  // Landing: Enter to continue (the hero advertises it).
  useEffect(() => {
    if (entered) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter") setEntered(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entered]);

  const handlers = useMemo(
    () => ({
      onOpenPalette: openPalette,
      onNewChat: () => {
        setEntered(true);
        newChat();
      },
      onToggleTheme: toggleTheme,
      onToggleDebug: () => setDebug(!debug),
      onOpenSettings: () => setSettingsOpen(true),
      onFocusComposer: () => composerRef.current?.focus(),
      onEscape: () => {
        setSettingsOpen(false);
        setDebugOpen(false);
        setPaletteOpen(false);
        setSidebarOpen(false);
      },
    }),
    [openPalette, newChat, toggleTheme, debug, setDebug],
  );
  useKeyboardShortcuts(handlers);

  const commands = useMemo<Command[]>(
    () => [
      { id: "new-chat", label: "New chat", group: "Actions", hint: "⌘⇧K", run: newChat },
      { id: "toggle-theme", label: "Toggle theme", group: "Actions", hint: "⌘J", run: toggleTheme },
      { id: "settings", label: "Open settings", group: "Actions", hint: "⌘,", run: () => setSettingsOpen(true) },
      { id: "toggle-debug", label: debug ? "Disable debug mode" : "Enable debug mode", group: "Actions", hint: "⌘D", run: () => setDebug(!debug) },
      ...workspaces.map((w) => ({ id: `ws-${w.id}`, label: `Go to ${w.label}`, group: "Workspaces", keywords: w.kind, run: () => selectWorkspace(w.id) })),
      ...conversations.filter((c) => c.messages.length > 0).map((c) => ({ id: `conv-${c.id}`, label: c.title, group: "Conversations", hint: "Conversation", run: () => selectConversation(c.id) })),
    ],
    [debug, workspaces, conversations, newChat, toggleTheme, setDebug, selectWorkspace, selectConversation],
  );

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  // Lock the page behind the mobile drawer so the body doesn't scroll under it (restored on close/unmount).
  useEffect(() => {
    if (!sidebarOpen) return;
    document.body.classList.add("voss-scroll-lock");
    return () => document.body.classList.remove("voss-scroll-lock");
  }, [sidebarOpen]);
  const dismissOnboarding = useCallback(() => {
    markOnboarded();
    setOnboarding(false);
  }, []);

  const onTouchStart = (e: TouchEvent): void => {
    const t = e.touches[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: TouchEvent): void => {
    const start = touchStart.current;
    const t = e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    if (Math.abs(t.clientY - start.y) > 40) return;
    if (start.x < 28 && dx > 60) setSidebarOpen(true);
    else if (sidebarOpen && dx < -60) setSidebarOpen(false);
    touchStart.current = null;
  };

  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence mode="wait">
        {!entered ? (
          <motion.div key="landing" variants={fadeRise} initial="hidden" animate="visible" exit="exit" style={{ height: "100%" }}>
            <Landing onEnter={() => setEntered(true)} />
          </motion.div>
        ) : (
          <motion.div key="app" className="voss-app" variants={fadeRise} initial="hidden" animate="visible" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <Sidebar open={sidebarOpen} onCloseMobile={closeSidebar} />
            <AnimatePresence>{sidebarOpen ? <motion.div className="voss-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeSidebar} aria-hidden /> : null}</AnimatePresence>
            <div className="voss-main">
              <TopBar onOpenSettings={() => setSettingsOpen(true)} onOpenDebug={() => setDebugOpen(true)} onOpenPalette={openPalette} onToggleSidebar={() => setSidebarOpen((v) => !v)} />
              {ready ? <Conversation /> : <ThreadSkeleton />}
              <Composer ref={composerRef} />
            </div>
            <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            <DebugDrawer open={debugOpen} onClose={() => setDebugOpen(false)} />
            <CommandPalette open={paletteOpen} commands={commands} onClose={() => setPaletteOpen(false)} />
            <Onboarding open={onboarding} onDismiss={dismissOnboarding} />
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
}
